import React, { useEffect, useState } from "react";
import { PageHeader, StatusBadge, Modal, Toast, EmptyState } from "@/components/Layout";
import api from "@/lib/api";
import Select from "@/components/Select";

export default function LicensedEmployers() {
  const [rows, setRows] = useState(null);
  const [billingFrequencies, setBillingFrequencies] = useState([]);
  const [renewing, setRenewing] = useState(null);
  const [msg, setMsg] = useState({ text: "", type: "info" });

  const load = () => api.get("/licenses").then(r => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/licenses/billing-frequencies").then(r => setBillingFrequencies(r.data));
  }, []);
  if (!rows) return <div>Se încarcă...</div>;

  // Grupat pe CUI, nu pe denumire — CUI e identificatorul real al angajatorului
  // (licența depinde de CUI; denumirea e doar etichetă afișată).
  const byCui = {};
  rows.forEach(l => { (byCui[l.cui] = byCui[l.cui] || {company: l.company_name, licenses: []}); byCui[l.cui].licenses.push(l); });

  const openRenew = (l) => setRenewing({ id: l.id, license_key: l.license_key,
    valid_to: l.valid_to, unit_price_ron: l.plan_price_ron, bill_per_employment_count: l.bill_per_employment_count,
    billing_frequency: l.billing_frequency || "monthly",
    billing_anchor_date: l.billing_anchor_date || new Date().toISOString().slice(0,10) });
  const saveRenew = async () => {
    if (renewing.unit_price_ron < 0) return setMsg({ text: "Prețul nu poate fi negativ", type: "error" });
    try {
      await api.put(`/licenses/${renewing.id}/renew`, {
        valid_to: renewing.valid_to,
        unit_price_ron: parseFloat(renewing.unit_price_ron) || 0,
        bill_per_employment_count: renewing.bill_per_employment_count,
        billing_frequency: renewing.billing_frequency,
        billing_anchor_date: renewing.billing_anchor_date,
      });
      setRenewing(null); load();
      setMsg({ text: "Licență reînnoită", type: "success" }); setTimeout(() => setMsg({ text: "" }), 1500);
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  return (
    <>
      <PageHeader title="Angajatori Licențiați" subtitle={`${Object.keys(byCui).length} angajatori`} />
      <Toast msg={msg.text} type={msg.type} />
      <div style={{display:"grid",gap:14}}>
        {Object.entries(byCui).map(([cui, {company, licenses}]) => (
          <div key={cui} className="card fade-up" style={{padding:20}} data-testid={`licensed-employer-${cui}`}>
            <div style={{fontSize:16,fontWeight:700,color:"var(--navy)",marginBottom:10}}>{company} <span style={{fontSize:12,fontWeight:400,color:"#64748B"}}>CUI: {cui}</span></div>
            <div style={{display:"grid",gap:8}}>
              {licenses.map(l => (
                <div key={l.id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 12px",background:"#F8FAFC",borderRadius:6}} data-testid={`licensed-row-${l.license_key}`}>
                  <span style={{fontSize:11,color:"var(--blue-primary)",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",minWidth:180}}>{l.module_label}</span>
                  <StatusBadge status={l.status} />
                  <span className="badge badge-approved">{l.plan_label}</span>
                  <span style={{fontSize:12,color:"#64748B"}}>{l.plan_price_ron} Lei{l.bill_per_employment_count ? " / raport" : ""}</span>
                  <span style={{fontSize:12,color:"#64748B",marginLeft:"auto"}}>{l.valid_from} → {l.valid_to} · {l.days_left ?? "—"} zile rămase</span>
                  <button className="btn btn-secondary" style={{padding:"3px 10px",fontSize:12}}
                    onClick={()=>openRenew(l)} data-testid={`renew-${l.license_key}`}>Reînnoiește / Modifică preț</button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {Object.keys(byCui).length === 0 && <EmptyState text="Niciun angajator licențiat încă." />}
      </div>

      {renewing && (
        <Modal title={`Reînnoiește / Modifică — ${renewing.license_key}`} onClose={()=>setRenewing(null)}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Valabil până la</label>
              <input className="input" type="date" value={renewing.valid_to}
                onChange={e=>setRenewing({...renewing,valid_to:e.target.value})} data-testid="renew-valid-to" /></div>
            <div><label className="label label-req">Preț (Lei)</label>
              <input className="input" type="number" min="0" step="0.01" value={renewing.unit_price_ron}
                onChange={e=>setRenewing({...renewing,unit_price_ron:e.target.value})} data-testid="renew-price" />
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Prețul e înghețat pe această licență — nu se mai schimbă automat dacă modifici ulterior prețul planului din "Prețuri".</div>
            </div>
            <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
              <input type="checkbox" id="renew-bill-per-empl" checked={renewing.bill_per_employment_count}
                onChange={e=>setRenewing({...renewing,bill_per_employment_count:e.target.checked})}
                data-testid="renew-bill-per-employment" style={{marginTop:3}} />
              <label htmlFor="renew-bill-per-empl" style={{fontSize:13,cursor:"pointer"}}>Facturare per raport de muncă</label>
            </div>
            <div className="grid-2">
              <div><label className="label label-req">Frecvență facturare</label>
                <Select className="select" value={renewing.billing_frequency}
                  onChange={e=>setRenewing({...renewing,billing_frequency:e.target.value})} data-testid="renew-billing-frequency">
                  {billingFrequencies.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
                </Select></div>
              <div><label className="label label-req">Data de facturare</label>
                <input className="input" type="date" value={renewing.billing_anchor_date}
                  onChange={e=>setRenewing({...renewing,billing_anchor_date:e.target.value})} data-testid="renew-billing-anchor" /></div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setRenewing(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={saveRenew} data-testid="save-renew">Salvează</button>
          </div>
        </Modal>
      )}
    </>
  );
}
